import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * PR-2: pasek postepu dlugiego zadania. Tor 8px, promien --radius-pill, tlo
 * --surface-sunken, wypelnienie --primary. Nad paskiem wiersz: po lewej co dzieje sie
 * TERAZ, po prawej procent w --text-muted.
 *
 * PR-3: pasek NIGDY nie zaslania ekranu - siedzi w karcie, w ktorej pojawi sie wynik.
 * Blokujaca nakladka pelnoekranowa jest zabroniona.
 * PR-5: postep oglaszamy czytnikowi OSZCZEDNIE - co ~10%, nie co 1%. Inaczej czytnik
 * mowi bez przerwy i nie da sie go sluchac.
 */

/** Co ile procent odzywa sie obszar aria-live (PR-5). */
const KROK_OGLOSZENIA = 10;

export function ProgressBar({
  etykieta,
  procent,
  akcja,
}: {
  /** CP-8: nazwij czynnosc po polsku, np. "Przetwarzanie 3 z 7". */
  etykieta: string;
  /** `undefined` = postepu nie da sie zmierzyc (pasek nieokreslony). */
  procent?: number;
  /** PR-3: "Anuluj", jesli zadanie da sie przerwac. */
  akcja?: ReactNode;
}): React.JSX.Element {
  const nieokreslony = procent === undefined;
  const wartosc = nieokreslony ? 0 : Math.max(0, Math.min(100, Math.round(procent)));
  const [ogloszenie, setOgloszenie] = useState("");
  const ostatnioRef = useRef(-1);
  const idEtykiety = useId();

  useEffect(() => {
    if (nieokreslony) return;
    const prog = Math.floor(wartosc / KROK_OGLOSZENIA);
    if (prog === ostatnioRef.current) return;
    ostatnioRef.current = prog;
    setOgloszenie(`${etykieta}, ${wartosc}%`);
  }, [nieokreslony, wartosc, etykieta]);

  return (
    <div className={`progress-block${nieokreslony ? " progress-block--nieokreslony" : ""}`}>
      <div className="progress-meta">
        <span className="progress-meta__label" id={idEtykiety}>
          {etykieta}
        </span>
        {!nieokreslony && (
          // aria-hidden, bo te sama liczbe niesie aria-valuenow toru - inaczej czytnik
          // przeczytalby procent dwa razy.
          <span className="progress-meta__pct" aria-hidden="true">
            {wartosc}%
          </span>
        )}
      </div>

      <div
        className="progress-track"
        role="progressbar"
        aria-labelledby={idEtykiety}
        aria-valuemin={nieokreslony ? undefined : 0}
        aria-valuemax={nieokreslony ? undefined : 100}
        aria-valuenow={nieokreslony ? undefined : wartosc}
        aria-busy={nieokreslony || undefined}
      >
        <div
          className={`progress-fill${nieokreslony ? " progress-fill--indeterminate" : ""}`}
          style={nieokreslony ? undefined : { width: `${wartosc}%` }}
        />
      </div>

      {/* PR-5: przy ograniczonym ruchu pasek nieokreslony zamienia sie w tekst.
          Przelacznikiem jest CSS (@media prefers-reduced-motion), nie JS - dzieki temu
          zmiana ustawienia systemowego dziala od razu, bez przeladowania. */}
      <p className="progress-block__fallback">Trwa przetwarzanie...</p>

      {akcja && <div className="progress-actions">{akcja}</div>}

      <span className="tylko-dla-czytnika" aria-live="polite">
        {ogloszenie}
      </span>
    </div>
  );
}
